// OLMPS v10 — LIFE ← NOW → SELF. Monumental, restrained.
// Two palettes over one token set. Dark (default): navy / near-black world,
// warm ivory type, gold for the few things that matter. Light: sunny sky-blue
// world, white glass, deep-navy type, deeper gold. Token NAMES keep their dark-mode meaning (`night`
// is "the page", `ivory` is "the type") so components read the same in both.
// Sector colours stay as small readable accents, never full-screen fills.
// See docs/redesign/reference/ and docs/redesign/CANON_FOR_UI.md.
import { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import type { Sector } from '../domain/olmps';

export type ThemeMode = 'dark' | 'light';
/** What the user picked: Night (dark), Day (light) or Live (follow the local clock). */
export type ThemePref = 'dark' | 'light' | 'live';

export interface V10Palette {
  mode: ThemeMode;
  // World
  night: string; // the page
  navy: string; // panels sit on this
  navyRaised: string; // cards
  navyLine: string; // hairline gold rule
  lineFaint: string; // even fainter divider
  glass: string;
  glassStrong: string;
  paper: string; // light surface that hosts the classic (light-themed) tools
  track: string; // empty progress track
  dotOff: string; // inactive pager dot
  scrim: [string, string, string]; // gradient over the Mountain art (top→bottom)
  scrimSelf: [string, string, string];
  pageGradient: [string, string, string]; // page background, top→bottom (never a flat fill)
  panelGradient: [string, string]; // glass panel fill, top→bottom
  panelHighlight: string; // 1px inner top highlight on panels
  glowSoft: string; // ambient drop glow under panels
  // Type
  ivory: string;
  ivorySoft: string;
  ivoryFaint: string;
  // Accent
  gold: string;
  goldBright: string;
  goldDeep: string;
  goldGlow: string;
  onGold: string; // text on a solid gold button
  // State
  onPace: string;
  behind: string;
  lapsed: string;
  malady: string;
  maladyRed: string;
  maladyText: string;
}

export const v10Dark: V10Palette = {
  mode: 'dark',
  night: '#070b17',
  navy: '#0d1426',
  navyRaised: '#141d35',
  navyLine: 'rgba(214,180,106,0.22)',
  lineFaint: 'rgba(214,180,106,0.12)',
  glass: 'rgba(13,20,38,0.72)',
  glassStrong: 'rgba(10,15,30,0.88)',
  paper: '#efe7d4',
  track: 'rgba(243,234,215,0.15)',
  dotOff: 'rgba(243,234,215,0.25)',
  scrim: ['rgba(7,11,23,0.85)', 'rgba(7,11,23,0.62)', 'rgba(7,11,23,0.95)'],
  scrimSelf: ['rgba(7,11,23,0.75)', 'rgba(7,11,23,0.2)', 'rgba(7,11,23,0.9)'],
  pageGradient: ['#0c1327', '#070b17', '#04060e'],
  panelGradient: ['rgba(30,42,78,0.78)', 'rgba(12,18,38,0.82)'],
  panelHighlight: 'rgba(240,207,133,0.28)',
  glowSoft: 'rgba(0,0,0,0.45)',

  ivory: '#f3ead7',
  ivorySoft: 'rgba(243,234,215,0.72)',
  ivoryFaint: 'rgba(243,234,215,0.45)',

  gold: '#d6b46a',
  goldBright: '#f0cf85',
  goldDeep: '#a8843f',
  goldGlow: 'rgba(240,207,133,0.45)',
  onGold: '#070b17',

  onPace: '#9ccf6a',
  behind: '#e6a34a',
  lapsed: '#e0574f',
  malady: '#2a2226',
  maladyRed: '#b8342c',
  maladyText: '#f08a82',
};

export const v10Light: V10Palette = {
  mode: 'light',
  night: '#e1f0ff', // the page (bottom of the sky; never cream)
  navy: '#f4f9ff',
  navyRaised: '#ffffff',
  navyLine: 'rgba(30,90,170,0.28)',
  lineFaint: 'rgba(30,90,170,0.14)',
  glass: 'rgba(255,255,255,0.78)',
  glassStrong: 'rgba(255,255,255,0.93)',
  paper: '#f4f9ff',
  track: 'rgba(17,40,85,0.14)',
  dotOff: 'rgba(17,40,85,0.24)',
  scrim: ['rgba(225,240,255,0.80)', 'rgba(225,240,255,0.70)', 'rgba(225,240,255,0.95)'],
  scrimSelf: ['rgba(225,240,255,0.55)', 'rgba(225,240,255,0.25)', 'rgba(225,240,255,0.85)'],
  pageGradient: ['#5aa9f0', '#9fd0ff', '#e8f4ff'],
  panelGradient: ['rgba(255,255,255,0.92)', 'rgba(240,248,255,0.88)'],
  panelHighlight: 'rgba(255,255,255,0.95)',
  glowSoft: 'rgba(30,90,170,0.20)',

  ivory: '#0f1f45',
  ivorySoft: 'rgba(15,31,69,0.76)',
  ivoryFaint: 'rgba(15,31,69,0.52)',

  gold: '#e8b21e',
  goldBright: '#f4c21f',
  goldDeep: '#c9940a',
  goldGlow: 'rgba(244,194,31,0.42)',
  onGold: '#1a2340',

  onPace: '#3f8a1c',
  behind: '#b86a10',
  lapsed: '#c6382f',
  malady: '#f3e3e6',
  maladyRed: '#b8342c',
  maladyText: '#a52d26',
};

/** Static dark tokens — for module-level constants that cannot use the hook. */
export const v10 = v10Dark;

// ---- Theme store (device-local, synchronous on web) -------------------------
// Pref: 'dark' (Night) | 'light' (Day) | 'live' (mirror the local clock:
// 07:00–18:59 is day → light, otherwise night → dark; re-evaluated each minute).
const KEY = 'olmps.theme';
const isDaytime = () => {
  const h = new Date().getHours();
  return h >= 7 && h < 19;
};
const resolve = (p: ThemePref): ThemeMode => (p === 'live' ? (isDaytime() ? 'light' : 'dark') : p);
let pref: ThemePref = (() => {
  if (Platform.OS !== 'web') return 'dark';
  try {
    const raw = window.localStorage.getItem(KEY);
    return raw === 'light' || raw === 'live' ? raw : 'dark';
  } catch {
    return 'dark';
  }
})();
let mode: ThemeMode = resolve(pref);
const listeners = new Set<(m: ThemeMode) => void>();
const prefListeners = new Set<(p: ThemePref) => void>();
if (Platform.OS !== 'web') {
  AsyncStorage.getItem(KEY)
    .then((raw) => {
      if (raw === 'light' || raw === 'dark' || raw === 'live') setThemePref(raw);
    })
    .catch(() => {});
}
// Live mode: check the clock every minute.
setInterval(() => {
  if (pref !== 'live') return;
  const next = resolve(pref);
  if (next !== mode) {
    mode = next;
    listeners.forEach((l) => l(next));
  }
}, 60_000);

export function getThemeMode(): ThemeMode {
  return mode;
}
export function getThemePref(): ThemePref {
  return pref;
}

export function setThemePref(next: ThemePref): void {
  if (next !== pref) {
    pref = next;
    prefListeners.forEach((l) => l(next));
    try {
      if (Platform.OS === 'web') window.localStorage.setItem(KEY, next);
      else void AsyncStorage.setItem(KEY, next).catch(() => {});
    } catch {
      /* best-effort */
    }
  }
  const m = resolve(pref);
  if (m !== mode) {
    mode = m;
    listeners.forEach((l) => l(m));
  }
}
/** Back-compat: set Night/Day directly. */
export function setThemeMode(next: ThemeMode): void {
  setThemePref(next);
}

/** Effective mode (what is on screen) + a setter for Night/Day. */
export function useThemeMode(): [ThemeMode, (m: ThemeMode) => void] {
  const [m, setM] = useState(mode);
  useEffect(() => {
    listeners.add(setM);
    return () => {
      listeners.delete(setM);
    };
  }, []);
  return [m, setThemeMode];
}

/** The user's preference (Night / Day / Live) + setter. */
export function useThemePref(): [ThemePref, (p: ThemePref) => void] {
  const [p, setP] = useState(pref);
  useEffect(() => {
    prefListeners.add(setP);
    return () => {
      prefListeners.delete(setP);
    };
  }, []);
  return [p, setThemePref];
}

/** The live palette. Re-renders the caller when the mode flips. */
export function useV10(): V10Palette {
  const [m] = useThemeMode();
  return m === 'light' ? v10Light : v10Dark;
}

/** `const styles = useStyles()` where `const useStyles = makeStyles((t) => ({...}))`. */
export function makeStyles<T>(build: (t: V10Palette) => T): () => T {
  return function useStyles() {
    const t = useV10();
    return useMemo(() => build(t), [t]);
  };
}

export const SECTOR_COLOR: Record<Sector, string> = {
  S: '#EF9F27',
  H: '#E24B4A',
  A: '#9A92F0',
  P: '#4F9BEA',
  E: '#79B23A',
};

export const SECTOR_NAME: Record<Sector, string> = {
  S: 'Social',
  H: 'Health',
  A: 'Admin',
  P: 'Personal',
  E: 'Economic',
};

/** Serif display face already bundled for the old shell. */
export const DISPLAY_FONT = 'ZeraphineObsidianSerif-Regular';

export const radius = { card: 22, pill: 999, sheet: 28 } as const;

// ---- Legacy bridge ----------------------------------------------------------
// The classic tools were written against `colors` from src/domain/tokens.ts
// (a light neumorphic scheme). `legacyColors(t)` gives every one of those keys
// a mode-correct value so a screen can be converted mechanically:
//   const { c } = useTheme();  …  colors.ink → c.ink   (inside makeStyles)
// Role notes: ink keys are TYPE, base/card/glassFill are the TONAL LADDER
// (each layer slightly lighter than its parent in dark mode too), primary/
// blue/blueText/lime/navActive are the single accent → GOLD.
export interface LegacyColors {
  primary: string; primaryDeep: string; primaryGlow: string; navActive: string;
  ink: string; inkSoft: string; text: string; textMuted: string; textFaint: string;
  base: string; baseLight: string; baseDark: string; shadowDark: string; shadowLight: string;
  chromeBg: string; sheetTop: string; sheetMid: string; sheetBottom: string;
  glassFill: string; glassFillStrong: string; glassStroke: string; glassRim: string;
  inputFill: string; inputRim: string; inputRimFocus: string;
  inputFillEmpty: string; inputFillEmptyPulse: string; inputRimEmpty: string;
  inputFillFilled: string; inputRimFilled: string; inputPlaceholderOnFill: string;
  card: string; cardBorder: string; glass: string; overlay: string;
  border: string; borderStrong: string; rimLight: string;
  success: string; warning: string; info: string;
  lime: string; limeSoft: string; blue: string; blueText: string; blueGlow: string;
  gearPeach: string; slate: string; purple: string;
  bgTop: string; bgBottom: string; danger: string; white: string;
}

export function legacyColors(t: V10Palette): LegacyColors {
  const dark = t.mode === 'dark';
  return {
    primary: t.gold, primaryDeep: t.goldDeep, primaryGlow: t.goldGlow, navActive: t.goldBright,
    ink: t.ivory, inkSoft: t.ivorySoft, text: t.ivory, textMuted: t.ivorySoft, textFaint: t.ivoryFaint,
    base: t.night, baseLight: t.navy, baseDark: dark ? '#05080f' : '#cfe4fa',
    shadowDark: dark ? 'rgba(0,0,0,0.55)' : 'rgba(30,90,170,0.22)', shadowLight: dark ? 'rgba(214,180,106,0.10)' : '#ffffff',
    chromeBg: t.night, sheetTop: t.navy, sheetMid: t.navy, sheetBottom: t.night,
    glassFill: dark ? '#182340' : '#f7fbff', glassFillStrong: dark ? '#1c2848' : '#ffffff',
    glassStroke: t.navyLine, glassRim: t.navyLine,
    inputFill: dark ? '#1c2848' : '#ffffff', inputRim: t.navyLine, inputRimFocus: t.gold,
    inputFillEmpty: dark ? '#4a1f1d' : '#f2938c', inputFillEmptyPulse: dark ? '#6b2622' : '#e5645a', inputRimEmpty: t.maladyRed,
    inputFillFilled: dark ? '#1f3d2a' : '#b6e7c4', inputRimFilled: t.onPace,
    inputPlaceholderOnFill: dark ? 'rgba(243,234,215,0.6)' : 'rgba(38,8,4,0.78)',
    card: t.navyRaised, cardBorder: t.navyLine, glass: t.glass, overlay: dark ? 'rgba(2,4,10,0.6)' : 'rgba(20,22,26,0.38)',
    border: t.lineFaint, borderStrong: t.navyLine, rimLight: dark ? 'rgba(243,234,215,0.06)' : 'rgba(255,255,255,0.95)',
    success: t.onPace, warning: t.behind, info: t.gold,
    lime: dark ? 'rgba(214,180,106,0.18)' : 'rgba(168,116,26,0.16)', limeSoft: dark ? 'rgba(214,180,106,0.10)' : 'rgba(168,116,26,0.09)',
    blue: t.gold, blueText: t.goldBright, blueGlow: t.goldGlow,
    gearPeach: t.navyRaised, slate: t.ivorySoft, purple: dark ? '#9A92F0' : '#5a50c8',
    bgTop: t.navy, bgBottom: t.night, danger: t.lapsed, white: dark ? t.ivory : '#ffffff',
  };
}

/** Flat-glass surface recipes replacing the neumorphic dual shadows. Same keys
 *  as the old `neu` export so `...neu.raised` call sites convert 1:1. */
export function neuFor(t: V10Palette) {
  const dark = t.mode === 'dark';
  const drop = dark ? 'rgba(0,0,0,0.45)' : 'rgba(30,90,170,0.18)';
  const rim = dark ? 'rgba(214,180,106,0.18)' : 'rgba(255,255,255,0.9)';
  return {
    raised: { boxShadow: `0 8px 24px ${drop}, inset 0 1px 0 ${rim}` },
    raisedLg: { boxShadow: `0 16px 40px ${drop}, inset 0 1px 0 ${rim}` },
    raisedSm: { boxShadow: `0 3px 10px ${drop}, inset 0 1px 0 ${rim}` },
    inset: { boxShadow: `inset 0 2px 6px ${dark ? 'rgba(0,0,0,0.5)' : 'rgba(30,90,170,0.18)'}` },
    carved: { boxShadow: `inset 0 1px 2px ${dark ? 'rgba(0,0,0,0.55)' : 'rgba(30,90,170,0.2)'}` },
    insetDeep: { boxShadow: 'none', borderWidth: 1, borderColor: t.navyLine },
    glowPrimary: { boxShadow: `0 6px 18px ${t.goldGlow}` },
  } as const;
}

export const fonts = { title: DISPLAY_FONT } as const;
export const radii = { shell: 30, nav: 26, tile: 16, panel: 22, pill: 999 } as const;
/** Layout sizes the shell primitives share (same keys as the old tokens). */
export const sizes = { gearTile: 44, navShape: 56, navBar: 88, hexagon: 28, panelBorder: 0 } as const;
/** Chart ramp — gold first, then the sector hues, then cool accents. */
export const series = ['#d6b46a', '#EF9F27', '#E24B4A', '#9A92F0', '#4F9BEA', '#79B23A', '#0d9488', '#c026d3'] as const;

export interface Theme {
  t: V10Palette;
  c: LegacyColors;
  neu: ReturnType<typeof neuFor>;
  fonts: typeof fonts;
  radii: typeof radii;
  series: typeof series;
  isDark: boolean;
}

/** One hook for everything a screen needs. Re-renders on mode flip. */
export function useTheme(): Theme {
  const t = useV10();
  return useMemo(() => ({ t, c: legacyColors(t), neu: neuFor(t), fonts, radii, series, isDark: t.mode === 'dark' }), [t]);
}

/** `const useStyles = makeThemedStyles((th) => StyleSheet.create({...}))` — gets the full Theme. */
export function makeThemedStyles<T>(build: (th: Theme) => T): () => T {
  return function useStyles() {
    const th = useTheme();
    return useMemo(() => build(th), [th]);
  };
}
